import verifyRoleAccess from '../services/verifyRoleAccess';
import getClosestVueParent from '../services/getClosestVueParent';
import { onBeforeUnmount, onMounted } from 'vue';

import { FormProvider } from '../components/TrForm.vue'

const SIZE = {
  large: 'input-lg',
  medium: '',
  small: 'input-sm'
};

export default {
  props: [
    'name',
    'disabled',
    'modelValue',
    'autofocus',
    'readonly',
    'required',
    'label',
    'size',
    'addonContent',
    'disableForRoles',
    'classes',
    'inputClasses',
    'dontUseColClasses',
    'noMargin',
    'warning',
    'error',
    'errorMessage',
    'errorToForceUntilIsDirty',
    'customValidation',
    'onBlur',
    'onValueChange',
    'runOnChangeOnWatch'
  ],
  inject: {
    tfFormInputs: {
      default: null
    },
    [FormProvider]: {
      default: null
    }
  },
  data: () => ({
    form: null,
    isValid: true,
    isDirty: false,
    isSubmitted: false,
    SIZE
  }),
  computed: {
    shouldDisable() {
      if (this.disabled) {
        return true;
      }

      if (this.disableForRoles) {
        return verifyRoleAccess.isLoggedUserForbiddenToAccess(this.disableForRoles);
      }
    },
    hasLabel() {
      return Boolean(this.label || this.$slots.label);
    },
    containerClasses() {
      let containerClasses = this.dontUseColClasses ? '' : ' col-sm-12 col-md-6 ';
      containerClasses += this.classes;

      if ((!this.classes || typeof this.classes !== 'string' || !this.classes.includes('col-lg')) && !this.dontUseColClasses) {
        if (this.type === 'textarea') {
          containerClasses += ' col-lg-12';
        } else {
          containerClasses += ' col-lg-4';
        }
      }

      if (!this.isValid) {
        containerClasses += ' has-error';
      }

      if (!this.noMargin) {
        containerClasses += ' bottom-offset2';
      }

      return containerClasses;
    }
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue || this.type === 'radio') {
        this.validateInput(this.modelValue);
        if (this.runOnChangeOnWatch) {
          this.onValueChange();
        }
      }
    },
    error() {
      this.validateInput(this.modelValue);
    },
    errorToForceUntilIsDirty() {
      this.validateInput(this.modelValue);
    }
  },
  created() {
    if (this.tfFormInputs && this.tfFormInputs.register) {
      let unregisterGlobalProvider;

      onMounted(() => {
        unregisterGlobalProvider = this.tfFormInputs.register(this);

        onBeforeUnmount(() => {
          unregisterGlobalProvider();
        })
      });
    }

    if (this[FormProvider]) {
      let unregisterFormProvider;

      onMounted(() => {
        unregisterFormProvider = this[FormProvider].register(this);

        onBeforeUnmount(() => {
          unregisterFormProvider();
        })
      });

    }
  },
  mounted() {
    this.form = getClosestVueParent.getByName(this, 'TrForm');
    this.validateInput(this.modelValue);
  },
  methods: {
    focus() {
      this.getInput().focus();
    },
    getInput() {
      return this.$el.querySelector('input:not(.hint), select');
    },
    setAsInvalid() {
      this.isValid = false;
    },
    setAsDirty() {
      this.isDirty = true;
      this.validateInput(this.modelValue);
    },
    forceValidation() {
      return this.validateInput(this.modelValue, true);
    },
    updateValue(value) {
      if (this.shouldDisable) {
        return;
      }

      if (this.type === 'currency') {
        this.lastInternalValue = value;
        value = this._getFormattedValue(value);
      }

      this.isDirty = true;
      this.validateInput(value);
      this.$emit('update:modelValue', value);

      if (this.onValueChange && !this.runOnChangeOnWatch) {
        this.onValueChange(value);
      }
    },
    validateInput(value, forceValidation) {
      let isValid = true;

      if (this.errorToForceUntilIsDirty && !this.isDirty) {
        this.isValid = false;
        return this.isValid;
      }

      if (this.form && this.form.hideErrosUntilIsDirty &&
        !this.isDirty && !this.form.isSubmitted && !forceValidation) {
        this.isValid = true;
        return this.isValid;
      }

      if (!value && this.required) {
        isValid = false;
      }

      if (value) {

        if (this.type !== 'radio') {
          if (this.maxlength && value.length > this.maxlength) {
            isValid = false;
          }

          if (this.exactLength && value.length !== this.exactLength) {
            isValid = false;
          }

          if (this.pattern && !RegExp(this.pattern).test(value)) {
            isValid = false;
          }
        }

        if (this.customValidation) {
          const customValidationResult = this.customValidation(value);
          if (typeof customValidationResult === 'object' && customValidationResult.then) {
            return customValidationResult.then(result => {
              if (this.shouldSetIsValid(result, forceValidation)) {
                this.isValid = result;
              }
              return result;
            });
          }

          if (!customValidationResult) {
            isValid = false;
          }
        }

        if (forceValidation) {
          this.isValid = isValid;
        }
      }

      if (this.error) {
        isValid = false;
      }

      if (this.shouldSetIsValid(isValid)) {
        this.isValid = isValid;
      }

      return this.isValid;
    },
    validateInputAsync() {
      return new Promise((resolve, reject) => {
        const result = this.validateInput(this.modelValue);

        if (typeof result === 'object') {
          return result.then(validationResult => {
            this._resolveOrRejectByValidationResult(resolve, reject, validationResult);
          });
        } else {
          this._resolveOrRejectByValidationResult(resolve, reject, result);
        }
      });
    },
    copy() {
      const input = this.getInput();
      input.select();
      document.execCommand('Copy');
    },
    shouldSetIsValid(isValid, forceValidation) {
      return Boolean(isValid || (this.isDirty || (this.form && this.form.isSubmitted) || this.error || forceValidation));
    },
    _resolveOrRejectByValidationResult(resolve, reject, validationResult) {
      if (validationResult) {
        resolve();
      } else {
        reject();
      }
    },
  }
}
